import type { ComponentType } from 'react';
import { createContext, useContext } from 'react';

import { useLanguagesEventSourcing } from 'stream/languages/useLanguagesEventSourcing';

import type { ActionPickerConfig } from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import { MenuOptions } from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import useFeatureSwitch from 'hooks/useFeatureSwitch/useFeatureSwitch';
import useFlows from 'hooks/useFlows/useFlows';
import useFunctions from 'hooks/useFunctions';
import useHandoff from 'hooks/useHandoff';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import useSmsCredentials from 'hooks/useSmsCredentials';
import useTelephony from 'hooks/useTelephony';
import useVariantManagement from 'hooks/useVariantManagement';
import type { RichTextInputV2Props } from '../..';

type UseActionsOptions = RichTextInputV2Props['config'];

const isActionTagEnabled = (
  config: ActionPickerConfig | undefined,
  targetMenuOption: MenuOptions,
): boolean => {
  if (!config) {
    return true;
  }
  const menuConfig = config[targetMenuOption];
  return menuConfig ? menuConfig.select : false;
};

const useActions = (options?: UseActionsOptions) => {
  const { actionTag: actionTagConfig, isActionTagStatic } = options ?? {};

  // Feature flags
  const { isFeatureOn } = useFeatureSwitch();
  const { isProjectResourceEnabled } = useProjectResource();

  const isHandoffEnabled =
    isFeatureOn('call_handoff') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.HANDOFFS) &&
    isActionTagEnabled(actionTagConfig, MenuOptions.HANDOFF);
  const isFunctionsEnabled =
    isFeatureOn('function_calls') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.FUNCTIONS) &&
    isActionTagEnabled(actionTagConfig, MenuOptions.FUNCTIONS);
  const isSmsEnabled =
    isFeatureOn('sms_templates') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.SMS) &&
    isActionTagEnabled(actionTagConfig, MenuOptions.SMS);
  const isFlowsEnabled =
    isFeatureOn('jobs_to_be_done') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.FLOWS) &&
    isActionTagEnabled(actionTagConfig, MenuOptions.FLOWS);
  const isVariantAttributesEnabled =
    isFeatureOn('jupiter_variant_attributes') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.VARIANT_MANAGEMENT) &&
    isActionTagEnabled(actionTagConfig, MenuOptions.VARIANT_ATTRIBUTES);
  const isEntitiesEnabled = isActionTagEnabled(
    actionTagConfig,
    MenuOptions.ENTITIES,
  );
  const isVariablesEnabled =
    isFeatureOn('variables') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.FUNCTIONS) && // Using FUNCTIONS resource for now
    isActionTagEnabled(actionTagConfig, MenuOptions.VARIABLES);
  const { additionalLanguages } = useLanguagesEventSourcing();
  const isTranslationsEnabled =
    isFeatureOn('multilingual_config') &&
    additionalLanguages.length > 0 &&
    isActionTagEnabled(actionTagConfig, MenuOptions.TRANSLATIONS);

  // Hooks
  const { isLoadingFunctions } = useFunctions(isFunctionsEnabled);
  const { isLoadingHandoffs } = useHandoff(isHandoffEnabled);
  const { isLoadingConnectionToken } = useTelephony(
    isHandoffEnabled || isSmsEnabled,
  );
  const { isLoadingSmsCredentials } = useSmsCredentials(isSmsEnabled);
  const { isLoadingFlows } = useFlows(isFlowsEnabled);
  const { isVariantManagementLoading } = useVariantManagement(
    isVariantAttributesEnabled,
  );

  return {
    isHandoffEnabled,
    isFunctionsEnabled,
    isSmsEnabled,
    isFlowsEnabled,
    isVariantAttributesEnabled,
    isEntitiesEnabled,
    isVariablesEnabled,
    isTranslationsEnabled,
    isLoading:
      isLoadingFunctions ||
      isLoadingHandoffs ||
      isLoadingConnectionToken ||
      isLoadingSmsCredentials ||
      isLoadingFlows ||
      isVariantManagementLoading,
    isActionTagStatic,
    customState: options?.customContext || undefined,
  };
};

type ActionsContextType = ReturnType<typeof useActions>;

export const ActionsContext = createContext<ActionsContextType>(
  new Proxy({} as any, {
    get: () => {
      throw new Error('You are not within a ActionsContext');
    },
  }),
);

export const useActionsContext = () => useContext(ActionsContext);

type PassdownProps = {
  config?: RichTextInputV2Props['config'];
};

export const withActionsContext =
  <T extends {}>(
    Component: ComponentType<T>,
  ): ComponentType<T & PassdownProps> =>
  (props: T & PassdownProps) => {
    const { config } = props;
    const value = useActions({
      actionTag: config?.actionTag,
      isActionTagStatic: config?.isActionTagStatic,
      customContext: config?.customContext,
    });

    return (
      <ActionsContext.Provider value={value}>
        <Component {...(props as T)} />
      </ActionsContext.Provider>
    );
  };

export default useActions;
